/**
 * paramMatch.js — client-side mirror of the proxy's param resolver
 * (proxy-server/conformance/paramResolver.js), used to filter the requests
 * table by "does this request carry a param matching <pattern>".
 *
 * Match forms (search-friendly — more forgiving than the check engine's exact
 * default, since this drives a live search box):
 *   imafw_*  ·  *fw*   glob   → wildcard (anchored)
 *   /^_fw_/           regex  → a string wrapped in slashes
 *   imafw_             plain  → CONTAINS (case-insensitive) — so a bare prefix
 *                              still matches imafw_csid without a trailing *
 * Never throws.
 */

export function makeParamSearchMatcher(pattern) {
  const p = String(pattern == null ? '' : pattern).trim();
  if (!p) return null;
  const rx = p.match(/^\/(.+)\/([a-z]*)$/i);
  if (rx) { try { const re = new RegExp(rx[1], rx[2] || 'i'); return (k) => re.test(String(k)); } catch { /* fall through */ } }
  if (p.indexOf('*') !== -1) {
    const re = new RegExp('^' + p.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*') + '$', 'i');
    return (k) => re.test(String(k));
  }
  const low = p.toLowerCase();
  return (k) => String(k).toLowerCase().includes(low);
}

/** Collect every param KEY a request carries: URL query, GAM cust_params,
 *  JSON/OpenRTB body (nested), and form-encoded body (imafw_* et al.). */
export function paramKeysOf(row) {
  const keys = [];
  if (!row) return keys;
  try {
    const u = new URL(row.url);
    for (const k of u.searchParams.keys()) keys.push(k);
    const cp = u.searchParams.get('cust_params');
    if (cp) { try { for (const k of new URLSearchParams(cp).keys()) keys.push(k); } catch { /* ignore */ } }
  } catch { /* bad url */ }

  const cust = row.validData && row.validData.cust;
  if (cust && typeof cust === 'object') keys.push(...Object.keys(cust));

  const collectDeep = (o, seen) => {
    if (!o || typeof o !== 'object' || seen.has(o)) return;
    seen.add(o);
    if (Array.isArray(o)) { o.forEach((el) => collectDeep(el, seen)); return; }
    for (const k of Object.keys(o)) { keys.push(k); collectDeep(o[k], seen); }
  };
  const rbp = row.requestBodyParsed;
  const rb = row.requestBody;
  // Live rows (toNetworkRow) pre-parse a JSON POST body into an OBJECT on
  // `requestBody` and never set `requestBodyParsed`, so accept an object on
  // either field — otherwise a JSON body (e.g. SpringServe C2S ext.kvps) is
  // silently skipped and only URL query params are searchable.
  if (rbp && typeof rbp === 'object') collectDeep(rbp, new Set());
  else if (rb && typeof rb === 'object') collectDeep(rb, new Set());
  else if (typeof rb === 'string') {
    const s = rb.trim();
    if (s.startsWith('{')) { try { collectDeep(JSON.parse(s), new Set()); } catch { /* not json */ } }
    else if (s.indexOf('=') !== -1) { try { for (const k of new URLSearchParams(s).keys()) keys.push(k); } catch { /* ignore */ } }
  }
  return keys;
}

/** Does this request carry ANY param key matching the pattern? */
export function rowMatchesParam(row, pattern) {
  const match = makeParamSearchMatcher(pattern);
  if (!match) return false;
  try { return paramKeysOf(row).some(match); } catch { return false; }
}
